'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { captureError } from '@/shared/lib/errors/capture';

interface UseVideoPlayerOptions {
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onProgress?: (buffered: number) => void;
}

export function useVideoPlayer(options: UseVideoPlayerOptions = {}) {
  const {
    autoPlay = false,
    muted: initialMuted = false,
    loop = false,
    onEnded,
    onTimeUpdate,
    onProgress,
  } = options;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Play/Pause
  const play = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.play();
      setIsPlaying(true);
    } catch (err) {
      captureError(err, {
        component: 'VideoPlayer',
        action: 'play-video',
        severity: 'warning',
        skipPostHog: true,
      });
    }
  }, []);

  const pause = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Mute
  const mute = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = true;
    setIsMuted(true);
  }, []);

  const unmute = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      unmute();
    } else {
      mute();
    }
  }, [isMuted, mute, unmute]);

  // Volume
  const changeVolume = useCallback(
    (newVolume: number) => {
      if (!videoRef.current) return;
      const clampedVolume = Math.max(0, Math.min(1, newVolume));
      videoRef.current.volume = clampedVolume;
      setVolume(clampedVolume);
      if (clampedVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    },
    [isMuted]
  );

  // Seek
  const seek = useCallback(
    (time: number) => {
      if (!videoRef.current) return;
      const clampedTime = Math.max(0, Math.min(duration, time));
      videoRef.current.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    },
    [duration]
  );

  const seekPercent = useCallback(
    (percent: number) => {
      const time = (percent / 100) * duration;
      seek(time);
    },
    [duration, seek]
  );

  const skipForward = useCallback(
    (seconds: number = 10) => {
      seek(currentTime + seconds);
    },
    [currentTime, seek]
  );

  const skipBackward = useCallback(
    (seconds: number = 10) => {
      seek(currentTime - seconds);
    },
    [currentTime, seek]
  );

  // Fullscreen
  const enterFullscreen = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      const container = videoRef.current.parentElement;
      if (container?.requestFullscreen) {
        await container.requestFullscreen();
      }
      setIsFullscreen(true);
    } catch (err) {
      captureError(err, {
        component: 'VideoPlayer',
        action: 'enter-fullscreen',
        severity: 'warning',
        skipPostHog: true,
      });
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      setIsFullscreen(false);
    } catch (err) {
      captureError(err, {
        component: 'VideoPlayer',
        action: 'exit-fullscreen',
        severity: 'warning',
        skipPostHog: true,
      });
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  // Playback Rate
  const changePlaybackRate = useCallback((rate: number) => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
  }, []);

  // Event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      onEnded?.();
    };
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      onTimeUpdate?.(video.currentTime, video.duration);
    };
    const handleDurationChange = () => setDuration(video.duration);
    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / video.duration) * 100;
        setBuffered(bufferedPercent);
        onProgress?.(bufferedPercent);
      }
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleError = () => setError('Failed to load video');
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Set initial properties
    video.muted = initialMuted;
    video.loop = loop;
    if (autoPlay) {
      play();
    }

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [autoPlay, initialMuted, loop, onEnded, onTimeUpdate, onProgress, play]);

  return {
    videoRef,
    // State
    isPlaying,
    isMuted,
    volume,
    currentTime,
    duration,
    buffered,
    isFullscreen,
    playbackRate,
    isLoading,
    error,
    // Progress
    progress: duration > 0 ? (currentTime / duration) * 100 : 0,
    // Actions
    play,
    pause,
    togglePlay,
    mute,
    unmute,
    toggleMute,
    changeVolume,
    seek,
    seekPercent,
    skipForward,
    skipBackward,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    changePlaybackRate,
  };
}
